import clsx from 'clsx';
import type React from 'react';
import type {PropsWithChildren} from 'react';
import {
  ErrorOutlinedIcon,
  InfoOutlinedIcon,
  TaskAltIcon,
  WarningAmberOutlinedIcon,
} from '../Icon';
import {IconButton} from '../IconButton';
import {Paper} from '../Paper';
import styles from './Alert.module.scss';

// TODO: add support for <AlertTitle>

type Severity =
  | 'error'
  | 'info'
  | 'success'
  | 'warning'
  | 'primary'
  | 'message'
  | 'attention';

type IconMapping = Record<Severity, React.ReactNode>;

type AlertProps = {
  action?: React.ReactNode;
  className?: string;
  color?: Severity;
  icon?: React.ReactNode | false;
  iconMapping?: Partial<IconMapping>;
  onClose?: (event: React.SyntheticEvent) => void;
  role?: string;
  severity?: Severity;
  variant?: 'filled' | 'outlined' | 'standard';
} & PropsWithChildren;

const defaultIconMapping: IconMapping = {
  error: <ErrorOutlinedIcon />,
  info: <InfoOutlinedIcon />,
  success: <TaskAltIcon />,
  warning: <WarningAmberOutlinedIcon />,
  primary: <InfoOutlinedIcon />,
  attention: <InfoOutlinedIcon />,
  message: <ErrorOutlinedIcon />,
};

const Alert = ({
  action,
  className,
  color,
  icon,
  iconMapping = defaultIconMapping,
  children,
  onClose,
  role = 'alert',
  severity = 'success',
  variant = 'standard',
  ...props
}: AlertProps): React.JSX.Element => {
  if (onClose && !action) {
    action = (
      <IconButton
        icon={{icon: 'close', size: 'small'}}
        onClick={e => onClose(e)}
        className={styles.close}
      />
    );
  }

  return (
    <Paper
      elevation={0}
      role={role}
      className={clsx(
        className,
        styles.alert,
        styles[color || severity],
        styles[variant],
      )}
      {...props}
    >
      {icon !== false && (
        <div className={styles.icon}>{icon || iconMapping[severity]}</div>
      )}
      <div className={styles.message}>{children}</div>
      {action && <div className={styles.action}>{action}</div>}
    </Paper>
  );
};

export {Alert, type AlertProps};
